import React, { useEffect, useMemo, useState } from "react";
import { Loader, Message, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { EnvironmentalObjetivesform, Meta as MetaController, Site, PlanAnual as PlanAnualController } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { ConfirmModal } from "../../../Shared";
import {
  genOcurrencias,
  avanceMeta,
  vencidasMeta,
  estadoMeta,
} from "../../../../utils/dataAndFormulas/objectivesCalendarCalc";
import { fetchBaselineYearsByAspect, getBaselineYearForKpi } from "../../../../utils/dataAndFormulas/kpiBaselineYear";
import "./GoalsAndActions.scss";

const environmentalObjetivesFormController = new EnvironmentalObjetivesform();
const metaController = new MetaController();
const siteController = new Site();
const planAnualController = new PlanAnualController();

// Mismo piso que ObjectivesMatrix.js/StrategicPlan.js/PlanDocument.js si el
// sitio todavía no configuró un horizonte propio (Fase 4).
const MIN_HORIZON = 5;

// Año de línea base de un objetivo puntual — mismo criterio duplicado en
// cada componente del módulo (convención ya establecida).
function getBaseYearForObjetivo(o, formByAspect) {
  return o.baselineYear ?? getBaselineYearForKpi(o.aspect, o.kpiCode, formByAspect) ?? (o.year || 0) - 1;
}

const FRECUENCIAS = ["Única", "Semanal", "Quincenal", "Mensual", "Bimestral", "Trimestral", "Semestral", "Anual"];

const EMPTY_META = {
  objectiveId: "",
  fechaInicio: "",
  fechaFin: "",
  frecuencia: "Mensual",
  responsible: "",
  investment: "",
  description: "",
  calculationNote: "",
};

const MONTHS_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function fmtFecha(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${MONTHS_ES[Number(m) - 1]}-${y.slice(-2)}`;
}
function progressColor(av) {
  if (av >= 100) return "#7CC48A";
  if (av >= 50) return "#F0D479";
  if (av > 0) return "#EFA1AB";
  return "#D4E5F0";
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
// Una meta "pertenece" al plan de un año si tiene alguna ocurrencia
// programada en ese año — relación derivada, no una FK (spec v6.6).
function metaEnPlan(m, year) {
  return year === "todos" || (m.ocurrencias || []).some((x) => String(x.fecha).slice(0, 4) === String(year));
}
function formatFileSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function GoalsAndActions({ siteSelected }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [objectives, setObjectives] = useState([]);
  const [formByAspect, setFormByAspect] = useState({});
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingMetaId, setEditingMetaId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_META);
  const [expandedMetaId, setExpandedMetaId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  // Evidencias (📎) por meta.
  const [expandedEvidMetaId, setExpandedEvidMetaId] = useState(null);
  const [uploadingEvidenceMetaId, setUploadingEvidenceMetaId] = useState(null);
  const [deleteEvidenceTarget, setDeleteEvidenceTarget] = useState(null);
  // Plan anual (spec v6.6): filtro por año + estado de gestión por año.
  const [selectedYear, setSelectedYear] = useState("todos");
  const [estadosByYear, setEstadosByYear] = useState({});
  const [savingEstado, setSavingEstado] = useState(false);

  const load = async () => {
    if (!siteSelected || !accessToken) return;
    setLoading(true);
    try {
      const [objRes, siteRes, planesRes] = await Promise.all([
        environmentalObjetivesFormController.getEnvironmentalObjetivesFormsBySite(accessToken, siteSelected),
        siteController.getSite(accessToken, siteSelected),
        planAnualController.getPlanesAnuales(accessToken, siteSelected),
      ]);
      const objs = objRes.code === 200 ? objRes.objetives || [] : [];
      setObjectives(objs);
      setSite(siteRes || {});
      const planes = planesRes.code === 200 ? planesRes.planes || [] : [];
      setEstadosByYear(Object.fromEntries(planes.map((p) => [p.year, p.estado])));
      const formByAspectResult = await fetchBaselineYearsByAspect(
        accessToken,
        siteSelected,
        objs.filter((o) => o.active !== false).map((o) => o.aspect)
      );
      setFormByAspect(formByAspectResult);
    } catch (error) {
      console.error(error);
      setObjectives([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [siteSelected, accessToken]);

  const activeObjectives = useMemo(() => objectives.filter((o) => o.active !== false), [objectives]);

  const baseYear = useMemo(() => {
    if (activeObjectives.length === 0) return new Date().getFullYear();
    return Math.min(...activeObjectives.map((o) => getBaseYearForObjetivo(o, formByAspect)));
  }, [activeObjectives, formByAspect]);

  const horizonEnd = site?.horizonYear || baseYear + MIN_HORIZON;
  const planYears = useMemo(
    () => Array.from({ length: horizonEnd - baseYear + 1 }, (_, i) => baseYear + i),
    [baseYear, horizonEnd]
  );

  const setYearEstado = async (year, estado) => {
    setSavingEstado(true);
    try {
      await planAnualController.setPlanAnualEstado(accessToken, siteSelected, year, estado);
      setEstadosByYear((prev) => ({ ...prev, [year]: estado }));
    } catch (error) {
      console.error(error);
      toast.error(t("ga_toast_plan_anual_error"));
    } finally {
      setSavingEstado(false);
    }
  };

  const hint = useMemo(() => {
    if (!formData.fechaInicio || !formData.frecuencia) return "";
    const oc = genOcurrencias(formData.fechaInicio, formData.fechaFin || formData.fechaInicio, formData.frecuencia);
    return t("ga_hint_fechas_template")
      .replace("{n}", oc.length)
      .replace("{frecuencia}", formData.frecuencia.toLowerCase())
      .replace("{ini}", fmtFecha(formData.fechaInicio))
      .replace("{fin}", fmtFecha(formData.fechaFin || formData.fechaInicio));
  }, [formData.fechaInicio, formData.fechaFin, formData.frecuencia, translations]);

  const openAddForm = () => {
    setEditingMetaId(null);
    setFormData(EMPTY_META);
    setShowForm(true);
  };

  const openEditForm = (objectiveId, meta) => {
    setEditingMetaId(meta._id);
    setFormData({
      objectiveId,
      fechaInicio: String(meta.fechaInicio).slice(0, 10),
      fechaFin: String(meta.fechaFin).slice(0, 10),
      frecuencia: meta.frecuencia,
      responsible: meta.responsible || "",
      investment: meta.investment || "",
      description: meta.description || "",
      calculationNote: meta.calculationNote || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingMetaId(null);
    setFormData(EMPTY_META);
  };

  const submitForm = async () => {
    const { objectiveId, fechaInicio, frecuencia, description } = formData;
    const errores = [];
    if (!objectiveId) errores.push(t("ga_form_objective_label"));
    if (!fechaInicio) errores.push(t("ga_form_fecha_inicio_label"));
    if (!frecuencia) errores.push(t("ga_form_frecuencia_label"));
    if (!description) errores.push(t("sp_form_description_label"));
    if (errores.length) {
      toast.error(t("sp_toast_missing_fields_template").replace("{fields}", errores.join(", ")));
      return;
    }
    try {
      const payload = {
        description: formData.description,
        fechaInicio: formData.fechaInicio,
        fechaFin: formData.fechaFin || formData.fechaInicio,
        frecuencia: formData.frecuencia,
        calculationNote: formData.calculationNote,
        investment: formData.investment,
        responsible: formData.responsible,
      };
      if (editingMetaId) {
        await metaController.updateMeta(accessToken, editingMetaId, payload);
        toast.success(t("ga_toast_meta_updated"));
      } else {
        await metaController.createMeta(accessToken, objectiveId, payload);
        toast.success(t("sp_toast_meta_added"));
      }
      closeForm();
      load();
    } catch (error) {
      toast.error(error?.message || t("sp_toast_add_meta_error"));
    }
  };

  const openDeleteMeta = (metaId) => setDeleteTargetId(metaId);
  const closeDeleteMeta = () => setDeleteTargetId(null);
  const confirmDeleteMeta = async () => {
    try {
      await metaController.deleteMeta(accessToken, deleteTargetId);
      closeDeleteMeta();
      load();
    } catch (error) {
      console.error(error);
      toast.error(t("ga_toast_delete_error"));
    }
  };

  // Toggle optimista: actualiza el estado local con las mismas funciones de
  // cálculo que el render, y hace rollback si la request al backend falla —
  // igual criterio que toggleOc() del mockup, pero contra la API.
  const toggleOcurrencia = async (objectiveId, metaId, ocurrenciaId, nuevaCumplida) => {
    const snapshot = objectives;
    setObjectives((prev) =>
      prev.map((o) =>
        o._id !== objectiveId
          ? o
          : {
              ...o,
              metas: (o.metas || []).map((m) =>
                m._id !== metaId
                  ? m
                  : {
                      ...m,
                      ocurrencias: (m.ocurrencias || []).map((oc) =>
                        oc._id === ocurrenciaId ? { ...oc, cumplida: nuevaCumplida } : oc
                      ),
                    }
              ),
            }
      )
    );
    try {
      await metaController.toggleOcurrencia(accessToken, metaId, ocurrenciaId, nuevaCumplida);
    } catch (error) {
      console.error(error);
      toast.error(t("ga_toast_toggle_error"));
      setObjectives(snapshot);
    }
  };

  const handleUploadEvidence = async (metaId, file) => {
    if (!file) return;
    setUploadingEvidenceMetaId(metaId);
    try {
      const formData = new FormData();
      formData.append("files", file);
      await metaController.uploadEvidence(accessToken, metaId, formData);
      await load();
    } catch (error) {
      toast.error(error?.msg || t("ga_toast_evid_upload_error"));
    } finally {
      setUploadingEvidenceMetaId(null);
    }
  };

  const handleDownloadEvidence = async (metaId, evidenciaId, fileName) => {
    try {
      await metaController.downloadEvidence(accessToken, metaId, evidenciaId, fileName);
    } catch (error) {
      toast.error(t("ga_toast_evid_download_error"));
    }
  };

  const openDeleteEvidence = (metaId, evidenciaId, name) => setDeleteEvidenceTarget({ metaId, evidenciaId, name });
  const closeDeleteEvidence = () => setDeleteEvidenceTarget(null);
  const confirmDeleteEvidence = async () => {
    const { metaId, evidenciaId } = deleteEvidenceTarget;
    try {
      await metaController.deleteEvidence(accessToken, metaId, evidenciaId);
      closeDeleteEvidence();
      load();
    } catch (error) {
      toast.error(t("ga_toast_evid_delete_error"));
    }
  };

  if (loading) return <Loader active inline="centered" />;

  const hoy = todayIso();

  return (
    <div className="goals-actions">
      <div className="ga-head">
        <h1>{t("ga_title")}</h1>
        <p>{t("ga_subtitle")}</p>
      </div>

      {activeObjectives.length === 0 ? (
        <Message>
          <Message.Header>{t("sp_empty_title")}</Message.Header>
          <p>{t("sp_empty_body")}</p>
        </Message>
      ) : (
        <>
          <div className="ga-plan-anio-bar">
            <span className="ga-plan-anio-bar__label">{t("ga_plan_anio_label")}</span>
            {planYears.map((y) => (
              <button
                key={y}
                type="button"
                className={`ga-chip-btn ${selectedYear === y ? "on" : ""}`}
                onClick={() => setSelectedYear(y)}
              >
                {y}{estadosByYear[y] === "definido" ? " ✓" : ""}
              </button>
            ))}
            <button
              type="button"
              className={`ga-chip-btn ${selectedYear === "todos" ? "on" : ""}`}
              onClick={() => setSelectedYear("todos")}
            >
              {t("ga_plan_anio_all")}
            </button>
            {selectedYear !== "todos" && (
              <>
                <span
                  className={`ga-chip ${estadosByYear[selectedYear] === "definido" ? "ga-chip--ok" : "ga-chip--warn"}`}
                >
                  {estadosByYear[selectedYear] === "definido" ? t("ga_plan_anio_definido") : t("ga_plan_anio_actualizacion")}
                </span>
                <button
                  type="button"
                  className="ga-btn ga-btn--secondary ga-btn--sm"
                  disabled={savingEstado}
                  onClick={() => setYearEstado(selectedYear, estadosByYear[selectedYear] === "definido" ? "actualizacion" : "definido")}
                >
                  {estadosByYear[selectedYear] === "definido" ? t("ga_plan_anio_reopen") : t("ga_plan_anio_define")}
                </button>
              </>
            )}
          </div>

          <div className="ga-band ga-band--between">
            <span>{t("ga_band_title")}</span>
            <button type="button" className="ga-btn ga-btn--sm" onClick={openAddForm}>
              {t("sp_btn_add_meta")}
            </button>
          </div>

          {showForm && (
            <div className="ga-card ga-quickadd">
              <div className="ga-quickadd__grid">
                <div className="ga-field">
                  <label>{t("ga_form_objective_label")} *</label>
                  <select
                    value={formData.objectiveId}
                    disabled={!!editingMetaId}
                    onChange={(e) => setFormData({ ...formData, objectiveId: e.target.value })}
                  >
                    <option value="" disabled>{t("sp_form_select_objective")}</option>
                    {activeObjectives.map((o) => (
                      <option key={o._id} value={o._id}>
                        {t(o.action)} {o.source} — {o.reductionTons}%
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ga-field">
                  <label>{t("ga_form_fecha_inicio_label")} *</label>
                  <input type="date" value={formData.fechaInicio} onChange={(e) => setFormData({ ...formData, fechaInicio: e.target.value })} />
                </div>
                <div className="ga-field">
                  <label>{t("ga_form_fecha_fin_label")}</label>
                  <input type="date" value={formData.fechaFin} onChange={(e) => setFormData({ ...formData, fechaFin: e.target.value })} />
                </div>
                <div className="ga-field">
                  <label>{t("ga_form_frecuencia_label")}</label>
                  <select value={formData.frecuencia} onChange={(e) => setFormData({ ...formData, frecuencia: e.target.value })}>
                    {FRECUENCIAS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="ga-field">
                  <label>{t("responsible")}</label>
                  <input type="text" placeholder={t("sp_form_responsible_placeholder")} value={formData.responsible} onChange={(e) => setFormData({ ...formData, responsible: e.target.value })} />
                </div>
                <div className="ga-field">
                  <label>{t("investment")}</label>
                  <input type="text" placeholder={t("sp_form_investment_placeholder")} value={formData.investment} onChange={(e) => setFormData({ ...formData, investment: e.target.value })} />
                </div>
              </div>
              <div className="ga-field" style={{ marginTop: 12 }}>
                <label>{t("sp_form_description_label")} *</label>
                <input type="text" placeholder={t("sp_form_description_placeholder")} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ width: "100%" }} />
              </div>
              <div className="ga-field" style={{ marginTop: 12 }}>
                <label>{t("sp_form_calc_label")}</label>
                <input type="text" placeholder={t("sp_form_calc_placeholder")} value={formData.calculationNote} onChange={(e) => setFormData({ ...formData, calculationNote: e.target.value })} style={{ width: "100%" }} />
              </div>
              <div className="ga-quickadd__actions">
                <button type="button" className="ga-btn" onClick={submitForm}>
                  {editingMetaId ? t("update") : t("sp_btn_add_goal")}
                </button>
                <button type="button" className="ga-btn ga-btn--secondary" onClick={closeForm}>{t("cancel")}</button>
                <span className="ga-hint">{hint}</span>
              </div>
            </div>
          )}

          <div className="ga-card ga-card--scroll">
            <table className="ga-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>{t("sp_th_objective")}</th>
                  <th style={{ textAlign: "left" }}>{t("sp_form_description_label")}</th>
                  <th>{t("ga_th_program")}</th>
                  <th>{t("ga_th_avance")}</th>
                  <th>{t("sp_th_state")}</th>
                  <th>{t("ga_th_registro")}</th>
                  <th>{t("responsible")}</th>
                  <th>{t("investment")}</th>
                </tr>
              </thead>
              <tbody>
                {activeObjectives.flatMap((o) =>
                  (o.metas || []).filter((m) => metaEnPlan(m, selectedYear)).map((m) => {
                    const av = avanceMeta(m);
                    const venc = vencidasMeta(m, hoy);
                    const st = estadoMeta(m, hoy);
                    const oc = m.ocurrencias || [];
                    const hechas = oc.filter((x) => x.cumplida).length;
                    const isOpen = expandedMetaId === m._id;
                    const isEvidOpen = expandedEvidMetaId === m._id;
                    const evidencias = m.evidencias || [];
                    return (
                      <React.Fragment key={m._id}>
                        <tr>
                          <td className="ga-txt"><span className="ga-code">{o.code || o.kpiCode}</span></td>
                          <td className="ga-txt">
                            <div className="ga-obj-title"><strong>{m.code}</strong> {m.description}</div>
                            {m.calculationNote && <div className="ga-obj-sub">{m.calculationNote}</div>}
                          </td>
                          <td className="ga-td-prog">
                            {fmtFecha(m.fechaInicio)} → {fmtFecha(m.fechaFin)}
                            <div className="ga-prog-sub">{m.frecuencia} · {oc.length} {oc.length === 1 ? t("ga_fecha_singular") : t("ga_fecha_plural")}</div>
                          </td>
                          <td>
                            <div className="ga-bar">
                              <div className="ga-bar__fill" style={{ width: `${Math.min(av, 100)}%`, background: progressColor(av) }} />
                              <div className="ga-bar__lbl">{Math.round(av)}%</div>
                            </div>
                            <div className="ga-cal-sub">
                              {hechas}/{oc.length} {t("ga_cumplidas")}
                              {venc > 0 && <span className="ga-venc-inline"> · ⚠ {venc} {venc === 1 ? t("ga_vencida") : t("ga_vencidas")}</span>}
                            </div>
                          </td>
                          <td><span className={`ga-chip ga-chip--${st.cls.replace("ec-", "")}`}>{st.txt}</span></td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <button type="button" className="ga-icon-btn" title={t("ga_btn_calendar")} onClick={() => setExpandedMetaId(isOpen ? null : m._id)}>📅</button>
                            <button type="button" className="ga-icon-btn" title={t("ga_btn_evidencias")} onClick={() => setExpandedEvidMetaId(isEvidOpen ? null : m._id)}>
                              📎 {evidencias.length > 0 && <span className="ga-evid-count">{evidencias.length}</span>}
                            </button>
                            <button type="button" className="ga-icon-btn" title={t("update")} onClick={() => openEditForm(o._id, m)}><Icon name="pencil" /></button>
                            <button type="button" className="ga-icon-btn ga-icon-btn--danger" title={t("delete")} onClick={() => openDeleteMeta(m._id)}><Icon name="trash" /></button>
                          </td>
                          <td>{m.responsible || "—"}</td>
                          <td>{m.investment || "—"}</td>
                        </tr>
                        {isOpen && (
                          <tr className="ga-cal-row">
                            <td colSpan={8}>
                              <div className="ga-cal-head">{t("ga_cal_title_template").replace("{id}", m.code)} — {t("ga_cal_hint")}</div>
                              {oc.length === 0 ? (
                                <div className="ga-cal-empty">{t("ga_cal_empty")}</div>
                              ) : (
                                <div className="ga-oc-grid">
                                  {oc.map((x) => {
                                    const isVenc = !x.cumplida && x.fecha < hoy;
                                    return (
                                      <label
                                        key={x._id}
                                        className={`ga-oc-chip ${x.cumplida ? "done" : isVenc ? "venc" : ""}`}
                                        title={x.cumplida ? t("ga_oc_cumplida") : isVenc ? t("ga_oc_vencida") : t("ga_oc_programada")}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={!!x.cumplida}
                                          onChange={() => toggleOcurrencia(o._id, m._id, x._id, !x.cumplida)}
                                        />
                                        <span>{fmtFecha(x.fecha)}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                        {isEvidOpen && (
                          <tr className="ga-cal-row">
                            <td colSpan={8}>
                              <div className="ga-cal-head">{t("ga_evid_title_template").replace("{id}", m.code)}</div>
                              {evidencias.length === 0 ? (
                                <div className="ga-cal-empty">{t("ga_evid_empty")}</div>
                              ) : (
                                <div className="ga-evid-list">
                                  {evidencias.map((ev) => (
                                    <div className="ga-evid-item" key={ev._id}>
                                      <Icon name="paperclip" />
                                      <span className="ga-evid-item__name">{ev.name}</span>
                                      <span className="ga-evid-item__meta">{formatFileSize(ev.size)}</span>
                                      <button type="button" className="ga-icon-btn" title={t("ga_evid_download")} onClick={() => handleDownloadEvidence(m._id, ev._id, ev.name)}>
                                        <Icon name="download" />
                                      </button>
                                      <button type="button" className="ga-icon-btn ga-icon-btn--danger" title={t("delete")} onClick={() => openDeleteEvidence(m._id, ev._id, ev.name)}>
                                        <Icon name="trash" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <label className="ga-btn ga-btn--sm ga-btn--secondary ga-evid-upload">
                                {uploadingEvidenceMetaId === m._id ? t("ga_evid_uploading") : t("ga_evid_add")}
                                <input
                                  type="file"
                                  hidden
                                  disabled={uploadingEvidenceMetaId === m._id}
                                  onChange={(e) => {
                                    handleUploadEvidence(m._id, e.target.files?.[0]);
                                    e.target.value = "";
                                  }}
                                />
                              </label>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ConfirmModal
        open={!!deleteTargetId}
        title={t("delete")}
        content={t("ga_confirm_delete_meta")}
        onConfirm={confirmDeleteMeta}
        onCancel={closeDeleteMeta}
        danger
      />

      <ConfirmModal
        open={!!deleteEvidenceTarget}
        title={t("delete")}
        content={t("ga_confirm_delete_evid")}
        onConfirm={confirmDeleteEvidence}
        onCancel={closeDeleteEvidence}
        danger
      />
    </div>
  );
}
